import { Component, OnInit, inject } from '@angular/core';
import { ApiService } from '@core/providers/http';
import { ButtonModule } from 'primeng/button';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormGroup,
} from '@angular/forms';
import { BackButtonComponent } from '@app/shared/components/back-button/back-button.component';
import { Select } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { dateRangeValidator } from '@core/validators/date-range.validator';
import { RepresentanteCardComponent } from '../representante-card/representante-card.component';
import { AuthService } from '@core/service/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { FileUploadModule } from 'primeng/fileupload';
import { EmpresaService } from '@app/features-modules/bolsa-de-profissionais/api';
import { InputMaskModule } from 'primeng/inputmask';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import {
  EmpresaSectorService,
  RepresentacaoEmpresaService,
  TipoRepresentacaoService,
} from '@app/features-modules/bolsa-de-profissionais/services';
import { EnumService } from '@app/features-modules/gestao-bolseiros/components/api/services';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-add-representante',
  imports: [
    CommonModule,
    ButtonModule,
    ReactiveFormsModule,
    BackButtonComponent,
    Select,
    InputTextModule,
    CalendarModule,
    RepresentanteCardComponent,
    ConfirmDialog,
    ConfirmDialogModule,
    InputMaskModule,
    ToastModule,
    FileUploadModule,
    DatePickerModule,
  ],
  providers: [
    ApiService,
    EnumService,
    RepresentacaoEmpresaService,
    TipoRepresentacaoService,
    EmpresaService,
    EmpresaSectorService,
    ConfirmationService,
    AuthService,
  ],
  templateUrl: './add-representante.component.html',
  styleUrl: './add-representante.component.scss',
  standalone: true,
})
export class AddRepresentanteComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private empresaService = inject(EmpresaSectorService);
  private enumService = inject(EnumService);
  private representanteEmpresaService = inject(RepresentacaoEmpresaService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  tiposRepresentacao: any[] = [];
  representacaoForm: any;
  nacionalidades: any[] = [
    {
      id: 1,
      designacao: 'Nacional',
    },
    {
      id: 2,
      designacao: 'Estrangeira',
    },
  ];
  representantes: any[] = [];
  editIndex: number | null = null;
  private tipoRepresentacaoService = inject(TipoRepresentacaoService);
  file: any;
  selectedFile: any;
  imagePreview: string | ArrayBuffer | null = null;
  isInternalInstitution: boolean = false;
  profileEducationalInstitution: any;
  fotoPreviewMap: { [key: string]: string } = {};
  isLoading: boolean = false;
  isPatching = false;

  ngOnInit(): void {
    this.loadTiposRepresentacao();
    this.representacaoForm = this.fb.group({
      nome: ['', Validators.required],
      telefone: ['', [Validators.required]],
      bi: ['', Validators.required],
      emailRepresentante: ['', [Validators.required, Validators.email]],
      tipoRepresentacaoID: ['', Validators.required],
      ativo: [true],
      foto: [''],
      NacionalidadeEnum: [],
      usernameRepresentante: [''],
      empresaID: [this.empresaID, Validators.required],
    });

    this.representacaoForm
      .get('NacionalidadeEnum')
      ?.valueChanges.subscribe((value: any) => {
        if (this.isPatching) return;

        this.representacaoForm.patchValue({
          bi: '',
        });
        this.onNacionalidadeChange(value);
      });

    this.representacaoForm.controls['bi'].valueChanges.subscribe(() => {
      if (this.isPatching) return;

      this.representacaoForm.patchValue({
        nome: '',
        telefone: '',
        // emailRepresentante: '',
      });
    });
    this.loadNacionalidade();
    this.loadEmpresaDetails();
  }

  get f() {
    return this.representacaoForm.controls;
  }

  get empresaID(): number {
    return Number(this.route.snapshot.paramMap.get('id'));
  }

  loadNacionalidade() {
    this.enumService.getAllNacionalidadeEnum().subscribe((response) => {
      if (response?.data) {
        this.nacionalidades = Object.entries(response.data).map(
          ([key, value]) => ({
            id: Number(key),
            designacao: value,
          }),
        );
      }
    });
  }

  onSelectedFiles(event: { currentFiles: any }) {
    this.file = event.currentFiles[0];

    //Adicionar validação de tamanho da imagem (ex: máx. 2MB)
    const maxSizeMB = 2;
    if (this.file.size > maxSizeMB * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: 'Imagem muito grande',
        detail: `A imagem deve ter no máximo ${maxSizeMB}MB.`,
        life: 3000,
      });
      return;
    }

    const allowedExtensions = ['jpg', 'jpeg', 'png'];
    const extension = this.file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Extensão inválida',
        detail: 'A imagem deve ser JPG ou PNG.',
        life: 3000,
      });
      return;
    }

    const isImage = this.file.type.startsWith('image/');
    if (!isImage) {
      this.messageService.add({
        severity: 'error',
        summary: 'Formato inválido',
        detail: 'Apenas imagens são permitidas.',
        life: 3000,
      });
      return;
    }

    if (this.file) {
      this.selectedFile = this.file;
      this.representacaoForm.patchValue({ foto: this.file });

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(this.file);
    }
  }

  choose(event: any, callback: () => void) {
    callback();
  }

  loadEmpresaDetails(): void {
    this.empresaService
      .show(String(this.empresaID))
      .subscribe((response: any) => {
        this.profileEducationalInstitution = response;
      });
  }

  save(data: any) {
    this.confirmationService.confirm({
      header: 'Confirmar Representante?',
      message: `Tem certeza que deseja salvar?`,
      rejectButtonProps: {
        label: 'Cancelar',
        icon: 'pi pi-times',
        outlined: true,
        size: 'small',
      },
      acceptButtonProps: {
        label: 'Sim',
        icon: 'pi pi-check',
        size: 'small',
      },
      accept: () => {
        this.store(data);
        // this.candidatar()
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Cancelada',
          detail: 'Representantes cancelados',
          life: 3000,
        });
      },
    });
    // this.messageService.add({
    //   key: 'confirmDialog',
    //   sticky: true,
    //   severity: 'warn',
    //   summary: 'Confirmação',
    //   detail: 'Tem certeza que deseja salvar?',
    //   data: { data },
    // });
  }

  deleteRepresentante(representante: any, index: number) {
    this.confirmationService.confirm({
      header: 'Confirmar Exclusão?',
      message: `Tem certeza que deseja excluir ${representante.nome}?`,
      accept: () => {
        this.representantes.splice(index, 1);
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Representante excluído com sucesso!',
          life: 3000,
        });
      },
    });
  }

  onConfirmSave(data: any) {
    console.log(data);
    // this.store(data);
  }

  onRejectSave() {
    this.messageService.clear('confirmDialog');
  }

  onNacionalidadeChange(value: any) {
    this.isInternalInstitution = value === 1; // 'INTERNA' is selected
    if (this.isInternalInstitution) {
      ['nome', 'telefone', 'emailRepresentante'].forEach((control) => {
        this.representacaoForm.get(control)?.disable();
      });
    } else {
      ['nome', 'telefone', 'emailRepresentante'].forEach((control) => {
        this.representacaoForm.get(control)?.enable();
      });
    }
  }

  searchDocumentNumber() {
    const biVal = this.f['bi']?.value;
    if (!biVal) return;

    this.authService.consultarBi(biVal).subscribe((result) => {
      const isSuccess =
        result?.retorno?.codigo == 200 ||
        result?.data?.code == 200 ||
        result?.data?.codigo == 200;
      if (isSuccess) {
        const personData = result.data?.data || result.data || {};
        const firstName = personData.first_name || personData.nome || '';
        const lastName = personData.last_name || personData.sobrenome || '';
        const nomeCompleto = `${firstName} ${lastName}`.trim();

        const patchValues: any = {};
        if (nomeCompleto) {
          patchValues.nome = nomeCompleto;
        }
        if (personData.numeroContacto || personData.telefone) {
          patchValues.telefone =
            personData.numeroContacto || personData.telefone;
        }
        if (personData.email) {
          patchValues.emailRepresentante = personData.email;
        }

        this.representacaoForm.patchValue(patchValues);

        this.representacaoForm.controls['telefone'].enable();
        this.representacaoForm.controls['emailRepresentante'].enable();
      }
    });
  }

  loadTiposRepresentacao() {
    this.tipoRepresentacaoService.list().subscribe((response) => {
      this.tiposRepresentacao = response.data;
    });
  }

  store(data: any[]) {
    if (!data || data.length === 0) {
      console.error('Nenhum dado foi passado para o método store.');
      return;
    }

    this.isLoading = true; // Iniciar carregamento

    // Processar os representantes e criar as requisições
    const requests = this.createRequests(data);

    // Executar todas as requisições e processar os resultados
    this.executeRequests(requests, data.length);
  }

  private createRequests(data: any[]): any[] {
    return data.map((representante: any) => {
      const formData = this.buildFormData(representante);

      // Retornar a requisição como um observable
      return this.representanteEmpresaService
        .add(formData)
        .pipe(
          catchError((error) =>
            this.handleIndividualError(representante, error),
          ),
        );
    });
  }

  private buildFormData(representante: any): FormData {
    const formData = new FormData();

    try {
      // Construir o payload como string JSON
      const payload = {
        emailRepresentante: representante.emailRepresentante || '',
        nome: representante.nome || '',
        telefone: representante.telefone || '',
        usernameRepresentante: representante.usernameRepresentante || '',
        tipoRepresentacaoID:
          representante.tipoRepresentacaoID?.toString() || '',
        ativo: representante.ativo?.toString() || 'false',
        empresaID: representante.empresaID?.toString() || '',
        NacionalidadeEnum: representante.NacionalidadeEnum || null,
        bi: representante.bi || null,
      };

      // Adicionar campos ao FormData
      formData.append('payload', JSON.stringify(payload));

      // Adicionar arquivo, se existir
      if (representante.foto && representante.foto instanceof File) {
        formData.append('File', representante.foto);
      } else if (representante.foto) {
        console.error('Foto não é um arquivo válido:', representante.foto);
      }
    } catch (error) {
      console.error('Erro ao processar representante:', representante, error);
    }

    return formData;
  }

  private handleIndividualError(representante: any, error: any) {
    console.error(
      `Erro ao cadastrar representante ${representante.nome}:`,
      error,
    );
    this.messageService.add({
      severity: 'error',
      summary: 'Erro',
      detail: `Erro ao cadastrar representante ${representante.nome}.`,
      life: 3000,
    });
    return of(null); // Continua mesmo que haja erro
  }

  private executeRequests(requests: any[], totalRequests: number) {
    forkJoin(requests).subscribe(
      (results) => {
        this.isLoading = false; // Parar carregamento

        const successCount = results.filter((result) => result !== null).length;
        this.handleResultMessage(successCount, totalRequests);

        // Navegar após a conclusão
        this.router.navigate([
          '/bolsa-profissionais/empresas/detalhe-empresa',
          this.empresaID,
        ]);
      },
      (error) => {
        this.handleGlobalError(error);
      },
    );
  }

  private handleResultMessage(successCount: number, totalRequests: number) {
    if (successCount === totalRequests) {
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Todos os representantes foram cadastrados com sucesso!',
        life: 3000,
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Parcialmente concluído',
        detail: `Alguns representantes não foram cadastrados (${
          totalRequests - successCount
        } falhas).`,
        life: 3000,
      });
    }
  }

  private handleGlobalError(error: any) {
    console.error('Erro ao cadastrar representantes:', error);
    this.isLoading = false; // Parar carregamento
    this.messageService.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao cadastrar os representantes.',
      life: 3000,
    });
  }

  onSubmit() {
    if (this.representacaoForm.valid) {
      const value = this.representacaoForm.getRawValue();
      value.usernameRepresentante = !this.isInternalInstitution
        ? value.emailRepresentante
        : value.bi;

      if (this.editIndex !== null) {
        this.representantes[this.editIndex] = value;
        this.editIndex = null;
      } else {
        this.representantes.push(value);
      }
      this.representacaoForm.reset();
      this.isInternalInstitution = false;
      this.representacaoForm.patchValue({
        empresaID: this.empresaID,
        ativo: true,
      });

      this.imagePreview = null; // Limpa a imagem após o envio
      this.selectedFile = null; // Limpa o arquivo após o envio
      this.fotoPreviewMap = {}; // Limpa o mapa de pré-visualização
      this.representacaoForm.get('foto')?.reset(); // Limpa o campo de arquivo
    }
  }

  editRepresentante(representante: any, index: number) {
    this.isPatching = true;
    this.representacaoForm.patchValue(representante);
    this.editIndex = index;
    this.isPatching = false;
  }

  desativarRepresentante(representante: any) {
    // Logic for deactivating the representative
    console.log('Desativar', representante);
  }

  notificarRepresentante(representante: any) {
    // Logic for notifying the representative
    console.log('Notificar', representante);
  }
}
